import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { Truck, MessageSquare, AlertTriangle, CheckCircle2, Camera, ShieldAlert, PenLine, Image as ImageIcon, Eye } from "lucide-react";
import { useState } from "react";
import { PACKAGE_PHOTO_THRESHOLD } from "@/lib/marketplace";
import { HeldFundsInfo } from "@/components/HeldFundsInfo";
import { SafeTradingCard } from "@/components/SafeTradingCard";
import { ReportMenu } from "@/components/ReportMenu";

import { RatingForm } from "@/components/RatingForm";
import { PriceInline, useCurrency } from "@/lib/currency";

export const Route = createFileRoute("/_authenticated/transaction/$id")({
  head: ({ params }) => ({ meta: [{ title: `${params.id} — Transaction` }] }),
  component: TxView,
});

const STAGES = ["Accepted", "Packed", "Shipped", "Delivered", "Rated"] as const;

function TxView() {
  const { id } = Route.useParams();
  const { display, format } = useCurrency();
  const [stageIdx] = useState(3);


  const cardTitle = "Charizard PSA 9 · #8801";
  const cardSub = "Base Set #4/102";
  const cardPrice = 3932;
  const shippingCost = 18;
  const heldAmount = cardPrice + shippingCost;
  const sellerReceives = cardPrice + shippingCost;
  const requiresPackagePhotos = heldAmount >= PACKAGE_PHOTO_THRESHOLD;
  const fmt = (usd: number) => `$${usd.toLocaleString()} USD${display !== "USD" ? ` · ≈ ${format(usd)}` : ""}`;

  const [messages, setMessages] = useState([
    { from: "buyer", text: "Hey, planning to ship today?", at: "Mon 10:14" },
    { from: "seller", text: "Yes, dropping off this afternoon. Tracking incoming.", at: "Mon 11:02" },
    { from: "seller", text: "Tracking #: 9400111…2384. Packed in toploader + bubble wrap.", at: "Mon 14:38" },
  ]);
  const [draft, setDraft] = useState("");

  function trySend(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    setMessages((m) => [...m, { from: "buyer", text: draft, at: "now" }]);
    setDraft("");
  }

  return (
    <AppShell>
      <div className="mb-4 text-sm text-muted-foreground">{id}</div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">{cardTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {cardSub} · @cardking99 ⇄ @vintagecards · <HeldFundsInfo label={<>Pending payout <PriceInline usd={heldAmount} /></>} />
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="status-pill bg-success/15 text-success border border-success/30"><CheckCircle2 className="h-3 w-3" /> Delivered</span>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="panel p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-success" /> Payment status
          </div>
          <div className="mt-1 font-display text-2xl font-bold text-success">Paid direct via PayPal</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Buyer paid the seller directly via PayPal Goods &amp; Services. Want To Buy does not hold or release funds.
          </div>
        </div>
        <div className="panel p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <ShieldAlert className="h-3.5 w-3.5 text-warning" /> PayPal dispute window
          </div>
          <div className="mt-1 font-display text-2xl font-bold text-warning">180 days</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            If you paid via PayPal G&amp;S, PayPal Buyer Protection lets you open a dispute directly with PayPal for up to 180 days after payment. Want To Buy does not mediate disputes.
          </div>
        </div>
      </div>


      <div className="panel mt-6 p-5">
        <div className="flex items-center justify-between">
          {STAGES.map((s, i) => (
            <div key={s} className="flex flex-1 items-center">
              <div className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${
                i <= stageIdx ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>{i + 1}</div>
              {i < STAGES.length - 1 && <div className={`h-0.5 flex-1 ${i < stageIdx ? "bg-primary" : "bg-muted"}`} />}
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
          {STAGES.map(s => <span key={s} className="flex-1 text-center first:text-left last:text-right">{s}</span>)}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <section className="panel p-5">
            <h2 className="mb-3 font-display text-lg font-bold flex items-center gap-2"><Camera className="h-5 w-5 text-primary" /> Required photos</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <MediaSlot title="Card photos (6+)" subtitle="Front, back, all four corners — cert visible" done count={6} />
              {requiresPackagePhotos ? (
                <MediaSlot title="Sealed package — front + back" subtitle={`Required for orders over $${PACKAGE_PHOTO_THRESHOLD}`} done count={2} />
              ) : (
                <MediaSlot title="Sealed package photos" subtitle={`Not required — order under $${PACKAGE_PHOTO_THRESHOLD}`} optional />
              )}
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-md border border-border bg-background/40 p-3 text-xs text-muted-foreground">
              <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
              If anything looks wrong on arrival, take clear photos of the package and card before opening sleeves — these become part of your evidence if you need to open a PayPal Goods &amp; Services dispute.
            </div>
          </section>

          <section className="panel p-5">
            <h2 className="mb-3 font-display text-lg font-bold flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /> Shipping</h2>
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <Info label="Method" value="Express Tracked" />
              <Info label="Signature" value="Required" icon={<PenLine className="h-3 w-3" />} />
              <Info label="Carrier" value="USPS Priority" />
              <Info label="Tracking" value="9400 1111 …2384" />
              <Info label="Shipped" value="Mon, 14:38" />
              <Info label="Delivered" value="Wed, 10:22" />
            </div>
            <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3 border-t border-border pt-3">
              <Info label="Card price" value={fmt(cardPrice)} />
              <Info label="Shipping" value={fmt(shippingCost)} />
              <Info label="Total" value={fmt(heldAmount)} />
            </div>
          </section>

          <section className="panel group p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" /> Private thread</h2>
              <ReportMenu
                reportedUsername="vintagecards"
                context="message"
                contextId={id}
                defaultOrderId={id}
                reportLabel="Report a message"
                extra={[{ label: "View order details", onClick: () => { window.location.href = `/orders/${id}`; } }]}
                size="md"
              />
            </div>
            
            <div className="mb-2 flex items-start gap-2 rounded-md border border-border bg-background/40 px-3 py-2 text-[11px] text-muted-foreground">
              <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              Messages may be reviewed for policy compliance and dispute resolution.
            </div>
            <div className="space-y-2 rounded-md border border-border bg-background/40 p-3 max-h-72 overflow-y-auto">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "buyer" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    m.from === "buyer" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                  }`}>
                    <div>{m.text}</div>
                    <div className={`mt-1 text-[10px] ${m.from === "buyer" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.at}</div>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={trySend} className="mt-3 flex gap-2">
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Message…" className="h-10 flex-1 rounded-md border border-border bg-background px-3 text-sm focus:border-primary focus:outline-none" />
              <button className="btn-primary px-4 text-sm">Send</button>
            </form>
            <p className="mt-2 text-[11px] text-muted-foreground">
              PayPal Goods &amp; Services is strongly recommended for buyer and seller protection.
            </p>
          </section>

          <RatingForm
            transactionId={id}
            rated="vintagecards"
            role="buyer"
            outcome="completed"
          />

          <section className="panel border-warning/30 p-5">
            <h2 className="mb-3 font-display text-lg font-bold flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-warning" /> Problem with this transaction?</h2>
            <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-foreground/90">
              <p>
                Want To Buy is a connection platform and does not mediate disputes between users. If something went wrong with this transaction, please resolve it directly with your payment provider.
              </p>
              <p className="mt-2">
                <strong>If you paid via PayPal Goods &amp; Services</strong>, open a dispute in the PayPal Resolution Centre. Screenshot this message thread, the seller's mandatory timestamped photos, and any additional photos as evidence. See the <a href="/payment-safety" className="text-primary underline">Payment Safety page</a> for a step-by-step guide.
              </p>
              <p className="mt-2 text-muted-foreground">
                For platform-integrity issues — counterfeit sales, ban evasion, or harassment — use the Report action to submit an admin report.
              </p>
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <SafeTradingCard />
          <div className="panel p-5">
            <h3 className="font-display font-bold">Payment breakdown</h3>
            <div className="mt-2 font-display text-3xl font-bold"><PriceInline usd={heldAmount} /></div>
            <div className="text-xs text-muted-foreground">Paid direct buyer → seller via PayPal Goods &amp; Services</div>

            <div className="mt-3 border-t border-border pt-3 text-sm">
              <Row label="Card" value={fmt(cardPrice)} />
              <Row label="Shipping" value={fmt(shippingCost)} />
              <Row label="Seller received" value={fmt(sellerReceives)} bold />
            </div>
            <div className="mt-3 rounded-md border border-border bg-background/40 p-2 text-[11px] text-muted-foreground">
              Buyer protection is provided by PayPal Goods &amp; Services — open any dispute directly in your PayPal account. Want To Buy does not mediate transaction disputes.
            </div>
          </div>


          <div className="panel p-5">
            <h3 className="font-display font-bold">Counterparty</h3>
            <div className="mt-2 text-sm">@vintagecards · ⭐ 4.9 · 142 trades</div>
            <div className="text-xs text-muted-foreground">0 strikes</div>
          </div>
        </aside>
      </div>
      
    </AppShell>
  );
}

function MediaSlot({ title, subtitle, done, needed, optional, count }: { title: string; subtitle: string; done?: boolean; needed?: boolean; optional?: boolean; count?: number }) {
  return (
    <div className={`rounded-md border p-4 ${done ? "border-success/40 bg-success/5" : needed ? "border-warning/40 bg-warning/5" : optional ? "border-border bg-background/40 opacity-70" : "border-border"}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        {done && <CheckCircle2 className="h-4 w-4 text-success" />}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>
      {done ? (
        <div className="mt-3 grid grid-cols-3 gap-1">
          {Array.from({ length: count ?? 4 }).map((_, i) => (
            <div key={i} className="aspect-square rounded bg-background/60 grid place-items-center text-[10px] text-muted-foreground">
              <ImageIcon className="h-4 w-4" />
            </div>
          ))}
        </div>
      ) : (
        <button className="btn-ghost mt-3 flex w-full items-center justify-center gap-2 py-2 text-xs">
          <Camera className="h-3.5 w-3.5" /> {optional ? "Optional" : "Upload photos"}
        </button>
      )}
    </div>
  );
}

function Info({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono flex items-center gap-1">{icon}{value}</div>
    </div>
  );
}
function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return <div className={`flex justify-between py-1 ${bold ? "font-bold text-foreground" : "text-muted-foreground"}`}><span>{label}</span><span>{value}</span></div>;
}
